/**
 * Service layer — Titleblock Issuance domain.
 *
 * Wraps /api/sheet-issuance (Clerk-authed). Owns titleblock profiles (calibrated overlay
 * positions), the QC-preview value resolver, the post-time burn / unpost reset, and the
 * batch issued-PDF lookup used by the drawing-sheet download/export. See
 * docs/TITLEBLOCK_ISSUANCE.md.
 */

async function post(action, payload = {}) {
  const token = await window.__clerkGetToken()
  const res = await fetch('/api/sheet-issuance', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    body: JSON.stringify({ action, ...payload }),
  })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b.error || `sheet-issuance ${action} failed (${res.status})`)
  }
  return res.json()
}

/** Resolved issuance values for a single revision (QC preview). → { owned, values } */
export async function resolveIssuance(revisionId) {
  return post('resolve', { revisionId })
}

/**
 * Renders this revision's assigned note stickers for a read-only QC preview — same render
 * pipeline burnMilestone uses, so it's pixel-for-pixel what would stamp on post.
 * → [{ id, name, real_width_in, positions, png (base64), aspect }]
 */
export async function previewNoteStickers(revisionId) {
  const { notes } = await post('previewNotes', { revisionId })
  return notes || []
}

/**
 * Non-committal test burn for one sheet — runs the exact same issuance + note-sticker composite
 * `burnMilestone` does, but never uploads to storage or writes `sheet_issuances`, and works even
 * if the milestone isn't posted yet (today's date stands in for the not-yet-known posted date).
 * Returns a real PDF Blob, stamped "PREVIEW — NOT POSTED" so it can't pass for the real thing.
 */
export async function previewBurn(revisionId) {
  const token = await window.__clerkGetToken()
  const res = await fetch('/api/sheet-issuance', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    body: JSON.stringify({ action: 'previewBurn', revisionId }),
  })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b.error || `previewBurn failed (${res.status})`)
  }
  return res.blob()
}

/** All calibrated titleblock profiles. → { profiles } */
export async function listTitleblockProfiles() {
  const { profiles } = await post('listProfiles')
  return profiles || []
}

/** Create a profile. `profile` = { name, vis_w, vis_h, page_frame, layout, active? }. */
export async function saveTitleblockProfile(profile) {
  const { profile: p } = await post('saveProfile', { profile })
  return p
}

/** Patch a profile by id. */
export async function updateTitleblockProfile(id, patch) {
  const { profile } = await post('updateProfile', { id, patch })
  return profile
}

/** Delete a profile by id. */
export async function deleteTitleblockProfile(id) {
  await post('deleteProfile', { id })
}

/** Burn issuance blocks onto every owned sheet of a posted milestone. → { burned, skipped } */
export async function burnMilestoneIssuance(milestoneId) {
  return post('burnMilestone', { milestoneId })
}

/** Reset a milestone's issuances on unpost so a re-post re-stamps. → { reset } */
export async function resetMilestoneIssuance(milestoneId) {
  return post('resetMilestone', { milestoneId })
}

/** Map of revisionId → issued (posted) PDF URL, for the download/export override. */
export async function getIssuedPdfMap(revisionIds) {
  if (!revisionIds || !revisionIds.length) return {}
  const { issued } = await post('getIssued', { revisionIds })
  return issued || {}
}

/**
 * Live Site Plan Notes for AlderCAD, as plain structured blocks (no stamped image) — mirrored
 * into the model doc's siteNotesBlocks setting and rendered directly in the sheet's note-block
 * area. → [{ type: 'h1'|'h2'|'h3'|'row'|'bullet'|'para', text, numbered? }]
 */
export async function getSiteNotesBlocks(projectId) {
  const { blocks } = await post('siteNotesBlocks', { projectId })
  return blocks || []
}

/** The same read with the project's jurisdiction (the parcel zone's city, else the project
 *  city) → { blocks, jurisdiction } — AlderCAD gates Newport / Laguna items on it. */
export async function getSiteNotesAndJurisdiction(projectId) {
  const { blocks, jurisdiction, standards } = await post('siteNotesBlocks', { projectId })
  // `standards`: the metric development standards the parcel research resolves to
  // (api/_developmentStandards.js) — null when the server sent none
  return { blocks: blocks || [], jurisdiction: jurisdiction || '', standards: standards || null }
}

/* ── Note stickers (plan-note blocks: Site Plan Notes, etc.) ─────────────────── */

/** All note stickers. → array */
export async function listNoteStickers() {
  const { notes } = await post('listNotes')
  return notes || []
}

/** Create a note sticker. `note` = { name, template_html, template_css, target_sheet_templates, real_width_in?, positions?, data_source? } */
export async function saveNoteSticker(note) {
  const { note: n } = await post('saveNote', { note })
  return n
}

/** Patch a note sticker by id. */
export async function updateNoteSticker(id, patch) {
  const { note } = await post('updateNote', { id, patch })
  return note
}

/** Delete a note sticker by id. */
export async function deleteNoteSticker(id) {
  await post('deleteNote', { id })
}

// ── AlderCAD (docs/bim-plan/28 round 9) ─────────────────────────────────────────────────────

/**
 * An AlderCAD sheet's issue fields for a milestone (or, with none, the milestone of the sheet's
 * newest revision) — the same resolution the post-time stamp uses. Returned in the sheet's own
 * shape: { projectNo, issueDate, issueTitle, zoningNo, buildingNo, milestoneId, milestoneName },
 * or null when the sheet is on no milestone yet.
 */
export async function resolveSheetIssuance(drawingSheetId, sheetNumber, milestoneId = null) {
  const r = await post('resolveSheet', { sheetId: drawingSheetId, sheetNumber, milestoneId })
  if (!r?.values) return null
  return {
    projectNo: r.values.project_no || '', issueDate: r.values.issue_date || '', issueTitle: r.values.issue_title || '',
    zoningNo: r.values.zoning_no || '', buildingNo: r.values.building_no || '',
    milestoneId: r.milestone?.id || undefined, milestoneName: r.milestone?.name || undefined,
  }
}

/** Record AlderCAD sheets sent to a milestone (one row per sheet) — read back by Post Milestone. */
export async function recordSheetSends(rows) {
  return post('recordSends', { rows })
}

/**
 * For Post Milestone: each AlderCAD-sent sheet on the milestone and whether its model has been
 * saved again since it was sent → [{ sheetNumber, revisionId, conceptId, projectId, sentAt, changed }]
 */
export async function getMilestoneSendStatus(milestoneId) {
  const r = await post('sendStatus', { milestoneId })
  return r?.sheets || []
}
